import React, { useContext, useEffect, useState } from 'react';
import { t } from 'i18next';
import SelectInput, {
  SelectInputHelper
} from '../../common/SelectInput/SelectInput';
import './NeedHelpPage.scss';
import { Link, useNavigate } from 'react-router-dom';
import {
  InsuranceJourneyHelperText,
  PageAbsolutePath,
  ValidationConstants,
  ValidationMessages
} from '../../../consts/Common';
import { SiteContext } from '../../../context/SiteContext';
import TextInput from '../../common/TextInput/TextInput';
import {
  AboutYouModel,
  AddressDetails,
  CarInsuranceVehicleModel,
  InsuranceJourneyState,
  KeyValuePair,
  QuoteStatus,
  SelectOpt,
  VehicleModificationError,
  VehicleType
} from '../../common/CommonTypes';
import { Gender, Title } from '../../common/Constants';
import GenderSelection from '../../common/GenderSelection/GenderSelection';
import DateSelector from '../../common/DateSelector/DateSelector';
import {
  getDynamicHelpText,
  getInsuranceJourneyMinMaxDob,
  getMonthNames,
  isValidName,
  populateSelectOptions,
  setCountryCode
} from '../../common/HelperMethods';
import ClickableHelpText from '../../common/HelpText/ClickableHelpText';
import AddressManager from '../../common/Address/AddressManager';
import { ButtonContinue } from '../../common/Button/ButtonContinue';
import { VehicleApi } from '../../../api/VehicleApi';
import { VrnSearchStatus } from './InsuranceVehicleDetailsPage';
import Checkbox from '../../common/Checkbox/Checkbox';
import { CommonValidation } from '../../common/CommonValidation/commonValidation';
import RadioOptions from '../../common/RadioOptions/RadioOptions';
import { CarInsuranceApi } from '../../../api/CarInsuranceApi';
import DateInput from '../../common/DateInput/DateInput';
import ClientDateHandler from '../../common/ClientDateHandler/ClientDateHandler';
import useCarInsuranceController from '../../../hooks/insuranceJourney/useCarInsuranceController';
import { Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { useErrorBoundary } from 'react-error-boundary';

type YourDetailsFields = {
  dateOfBirth: Date | undefined;
  title: string;
  gender: string;
  firstName: string;
  lastName: string;
  email: string;
  selectedGender: string;
  mobilePhone: string;
  homePhone: string;
  expiryMonth: string;
  overnightParkedAddress: AddressDetails;
  coverStartDate: Date | undefined;
  vrnKnown: string;
  userVrn: string;
  manufacturer: string;
  model: string;
  fuelType: string;
  transmission: string;
  numberOfDoors: string;
  isItNewOrUsed: string;
  vehicleSpecification: string;
  yearOfManufacture: string;
  typeOfTrackingDevice: string;
  modifications: VehicleModificationError[];
  dealer: string;
  keysPickupDate: string;
  isFittedWithAntiTheftDevice: string;
  isVehicleModified: string;
  transmissionType: string;
  vehicle: VehicleType | undefined;
  marketingViaEmail: boolean;
  marketingViaPhone: boolean;
  marketingViaPost: boolean;
  marketingViaTextMessage: boolean;
};

type YourDetailsErrors = {
  dateOfBirth: string;
  title: string;
  gender: string;
  firstName: string;
  lastName: string;
  email: string;
  selectedGender: string;
  mobilePhone: string;
  homePhone: string;
  expiryMonth: string;
  overnightParkedAddress: AddressDetails;
  coverStartDate: string;
  vrnKnown: string;
  userVrn: string;
  manufacturer: string;
  model: string;
  fuelType: string;
  transmission: string;
  numberOfDoors: string;
  isItNewOrUsed: string;
  vehicleSpecification: string;
  yearOfManufacture: string;
  typeOfTrackingDevice: string;
  modifications: VehicleModificationError[];
  dealer: string;
  keysPickupDate: string;
  isFittedWithAntiTheftDevice: string;
  isVehicleModified: string;
  transmissionType: string;
  vehicle: VehicleType | undefined;
  marketingViaEmail: boolean;
  marketingViaPhone: boolean;
  marketingViaPost: boolean;
  marketingViaTextMessage: boolean;
  keepingYouInformedOptions: string;
  addressError: string;
};

const initialFormValues: YourDetailsFields = {
  dateOfBirth: undefined,
  coverStartDate: undefined,
  title: '',
  gender: '',
  lastName: '',
  firstName: '',
  email: '',
  selectedGender: '',
  mobilePhone: '',
  homePhone: '',
  expiryMonth: '',
  overnightParkedAddress: {},
  vrnKnown: '',
  userVrn: '',
  manufacturer: '',
  model: '',
  fuelType: '',
  transmission: '',
  numberOfDoors: '',
  isItNewOrUsed: '',
  vehicleSpecification: '',
  yearOfManufacture: '',
  typeOfTrackingDevice: '',
  modifications: [],
  dealer: '',
  keysPickupDate: '',
  isFittedWithAntiTheftDevice: '',
  isVehicleModified: '',
  transmissionType: '',
  vehicle: undefined,
  marketingViaEmail: false,
  marketingViaPhone: false,
  marketingViaPost: false,
  marketingViaTextMessage: false
};

const initialFormErrors: YourDetailsErrors = {
  coverStartDate: '',
  dateOfBirth: '',
  title: '',
  gender: '',
  lastName: '',
  firstName: '',
  email: '',
  selectedGender: '',
  mobilePhone: '',
  homePhone: '',
  expiryMonth: '',
  overnightParkedAddress: {},
  vrnKnown: '',
  userVrn: '',
  manufacturer: '',
  model: '',
  fuelType: '',
  transmission: '',
  numberOfDoors: '',
  isItNewOrUsed: '',
  vehicleSpecification: '',
  yearOfManufacture: '',
  typeOfTrackingDevice: '',
  modifications: [],
  dealer: '',
  keysPickupDate: '',
  isFittedWithAntiTheftDevice: '',
  isVehicleModified: '',
  transmissionType: '',
  vehicle: undefined,
  marketingViaEmail: false,
  marketingViaPhone: false,
  marketingViaPost: false,
  marketingViaTextMessage: false,
  keepingYouInformedOptions: '',
  addressError: ''
};

enum PageState {
  Initial,
  ValidatingAddress,
  UpdatingAddress,
  ValidatingCorrespondenceAddress,
  UpdatingCorrespondenceAddress,
  Redirecting
}

type YourDetailsProps = {
  pageName?: string;
};

const YourDetails = ({ pageName }: YourDetailsProps): JSX.Element => {
  const [dobIsValid, setDobIsValid] = useState(true);
  const [updateDobError, setUpdateDobError] = useState(false);
  const [pageState, setPageState] = useState(PageState.Initial);
  const transmissionList = ['Manual', 'Automatic'];
  const [vehicleSearchResult, setVehicleSearchResult] = useState(0);
  const [showSearchMessage, setShowSearchMessage] = useState(false);
  const [formValues, setFormValues] =
    useState<YourDetailsFields>(initialFormValues);
  const [formErrors, setFormErrors] =
    useState<YourDetailsErrors>(initialFormErrors);
  const [showNonSchemePopup, setShowNonSchemePopup] = useState(false);
  const [showManualSearchForm, setShowManualSearchForm] = useState(false);
  const { site } = useContext(SiteContext);
  const navigate = useNavigate();
  const { showBoundary } = useErrorBoundary();

  const { brand } = site;
  const token = site.user.token;

  const {
    coverStartDate,
    dateOfBirth,
    title,
    gender,
    lastName,
    firstName,
    email,
    mobilePhone,
    homePhone,
    expiryMonth,
    overnightParkedAddress,
    vrnKnown,
    userVrn,
    fuelType,
    dealer,
    manufacturer,
    model,
    numberOfDoors,
    transmissionType,
    vehicle,
    vehicleSpecification,
    marketingViaEmail,
    marketingViaPhone,
    marketingViaPost,
    marketingViaTextMessage
  } = formValues;

  const today = new Date();
  const { getNewQuote, saveQuote } = useCarInsuranceController();
  const { minDob, maxDob } = getInsuranceJourneyMinMaxDob();
  const monthNames = getMonthNames();
  const isAgeValid = (): boolean => {
    if (dateOfBirth === undefined) {
      return false;
    }

    const dob =
      typeof dateOfBirth === 'string' ? new Date(dateOfBirth) : dateOfBirth;

    let age = today.getFullYear() - dob.getFullYear();

    const monthDiff = today.getMonth() - dob.getMonth();

    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < dob.getDate())) {
      age--;
    }

    const result = age >= 17 && age <= 90;

    return result;
  };

  const { brandDisplayName } = brand;
  const lastNameRequiredMsg = t(
    'personal-details.lastname-required-validation'
  );

  const lastNameLimitError = t('personal-details.lastname-minchar-validation');
  const invalidEmailMsg = t(
    'personal-details.email-invalid-personal-details-validation'
  );
  const titleErrorMsg = 'Title is required';
  const dealerErrorMsg = 'Dealer is required';
  const genderErrorMsg = 'Gender is required';

  const emailRequiredMsg = t('personal-details.email-required-validation');
  const dobRequiredMsg = t('personal-details.dob-required-validation');
  const dobInvalidMsg = t('personal-details.dob-invalid-validation');
  const ageOutOfRange = 'Customer age must be between 17 to 90';
  const expiryMonthRequiredMsg =
    'Existing insurance policy expiry month is required';
  const mobilePhoneRequiredMsg = 'Mobile phone is required';
  const mobilePhoneInvalidMsg = 'Mobile phone is not valid';
  const [titleOptions, setTitleOptions] = useState<SelectOpt[]>([]);
  const [genderOptions, setGenderOptions] = useState<SelectOpt[]>([]);
  const [fuelTypes, setFuelTypes] = useState<SelectOpt[]>([]);
  const [dealers, setDealers] = useState<SelectOpt[]>([]);
  const [vehicleSpecificationsOptions, setvehicleSpecificationsOptions] =
    useState<SelectOpt[]>([]);
  const [numberOfDoorsValues, setNumberOfDoorsValues] = useState<SelectOpt[]>(
    []
  );
  const [manufacturersOptions, setManufacturersOptions] = useState<SelectOpt[]>(
    []
  );
  const [modelsOptions, setModelsOptions] = useState<SelectOpt[]>([]);

  const onBackendFailure = (err: string) => {
    showBoundary(new Error(err));
  };

  const loadAboutYouDropDownList = async () => {
    const controller = new AbortController();

    const setAboutYouDropDownlistSuccess = (aboutYouList: AboutYouModel) => {
      setTitleOptions(populateSelectOptions(aboutYouList.titles));
      setGenderOptions(populateSelectOptions(aboutYouList.genders));
    };

    await new CarInsuranceApi().GetAboutYouDropDownListsAsync(
      token,
      setAboutYouDropDownlistSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const loadVehicleSpecification = async () => {
    const controller = new AbortController();

    const setVehicleSpecificationsDropDownlistSuccess = (
      vehicleMakes: KeyValuePair[]
    ) => {
      setvehicleSpecificationsOptions(populateSelectOptions(vehicleMakes));
    };

    await new CarInsuranceApi().GetDropDownValuesForVehicleSpecificationsAsync(
      manufacturer,
      model,
      fuelType,
      transmissionType,
      numberOfDoors,
      token,
      setVehicleSpecificationsDropDownlistSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onFindVehicleBySelectOptions = (): void => {
    loadVehicleSpecification();
    if (vehicleSpecification.length === 0) {
      setVehicleSearchResult(VrnSearchStatus.ManualNotFound);
    } else {
      setVehicleSearchResult(VrnSearchStatus.ManualFound);
      setShowSearchMessage(false);
    }
  };

  useEffect(() => {
    if (titleOptions.length === 0) {
      loadAboutYouDropDownList();
      loadVehicleDetailsDropDownList();
      loadVehicleMakeDropDownList();
      onChangingManufacturer(site.brand.brandName.toUpperCase());
    }
  }, []);

  useEffect(() => {
    if (title === Title.Mr) onChangeGenderSelection(Gender.Male);
    if (title === Title.Miss || title === Title.Mrs || title === Title.Ms)
      onChangeGenderSelection(Gender.Female);
  }, [title]);

  useEffect(() => {
    if (manufacturer && manufacturer !== SelectInputHelper.NullValue()) {
      loadVehicleModelDropDownList();
    }
  }, [manufacturer]);

  useEffect(() => {
    showNoneSchemePopup(vehicle?.make ?? '');
  }, [vehicle?.make ?? '']);

  const showNoneSchemePopup = (make: string) => {
    if (
      make &&
      make !== SelectInputHelper.NullValue() &&
      make.toLowerCase() !== site.brand.brandName.toLowerCase() &&
      site.brand.brandName.toLowerCase() !== 'original'
    ) {
      setShowNonSchemePopup(true);
    }
  };

  //vehicle
  const loadVehicleDetailsDropDownList = async () => {
    const controller = new AbortController();

    const getPageDataSuccess = (dropdownValues: CarInsuranceVehicleModel) => {
      setDealers(populateSelectOptions(dropdownValues.dealers));
      setFuelTypes(populateSelectOptions(dropdownValues.fuelTypes));
      setNumberOfDoorsValues(
        populateSelectOptions(dropdownValues.numberOfDoors)
      );
    };

    await new CarInsuranceApi().GetDropDownValuesForVehicleDetailsAsync(
      site.brand.tiaProductCode,
      token,
      getPageDataSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const loadVehicleMakeDropDownList = async () => {
    const controller = new AbortController();

    const getPageDataSuccess = (vehicleMakes: KeyValuePair[]) => {
      setManufacturersOptions(populateSelectOptions(vehicleMakes));
    };

    await new CarInsuranceApi().GetDropDownValuesForVehicleMakeAsync(
      token,
      getPageDataSuccess,
      onBackendFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const loadVehicleModelDropDownList = async () => {
    const controller = new AbortController();

    const getVehicleModelDropDownlistSuccess = (
      vehicleModel: KeyValuePair[]
    ) => {
      setModelsOptions(populateSelectOptions(vehicleModel));
    };

    await new CarInsuranceApi().GetDropDownValuesForVehicleModelsAsync(
      manufacturer,
      token,
      getVehicleModelDropDownlistSuccess,
      onBackendFailure,
      controller.signal
    );
    return () => {
      controller.abort();
    };
  };

  const getManufacturerName = (value: string) => {
    return manufacturersOptions.find((m) => m.value === value)?.text ?? '';
  };

  const onChangingManufacturer = (value: string) => {
    const make = getManufacturerName(value);
    showNoneSchemePopup(make);
    const updatedFormValues = { ...formValues, manufacturer: value };
    setFormValues(updatedFormValues);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
  };

  const onChangeGenderSelection = (value: string) => {
    const updatedFormValues = { ...formValues, gender: value };
    setFormValues(updatedFormValues);
  };

  const onSelectChange = (value: string, key: string) => {
    const updatedFormValues = { ...formValues, [key]: value };

    setFormValues(updatedFormValues);
  };

  const onTextChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const updatedFormValues = { ...formValues, [key]: e.currentTarget.value };

    setFormValues(updatedFormValues);
  };

  const onChangeDateOfBirth = (value: Date | undefined) => {
    const updatedFormValues = { ...formValues, dateOfBirth: value };
    setFormValues(updatedFormValues);
  };

  const onChangeExpiryMonth = (value: string) => {
    const updatedFormValues = { ...formValues, expiryMonth: value };
    setFormValues(updatedFormValues);
  };

  const handlePhoneNumberChange = (
    e: React.FormEvent<HTMLInputElement>,
    key: string
  ): void => {
    const numericValue = e.currentTarget.value.replace(/[^0-9]/g, '');

    const updatedFormValues = { ...formValues, [key]: numericValue };

    setFormValues(updatedFormValues);
  };

  const handleAddressUpdate = (address: AddressDetails) => {
    const updatedFormValues = {
      ...formValues,
      overnightParkedAddress: address
    };
    setFormValues(updatedFormValues);
  };

  let parsedCoverStartDate = new Date();

  const onDateChange = (e: React.FormEvent<HTMLInputElement>, key: string) => {
    const value = (e.target as HTMLInputElement).value;
    const cdh = new ClientDateHandler();
    cdh.parseJSON(value);
    parsedCoverStartDate = cdh.getDate(false) ?? new Date();
    const updatedFormValues = { ...formValues, [key]: value };

    setFormValues(updatedFormValues);
  };

  const handleAddressValidate = (isValid: boolean) => {
    if (isValid) {
      setPageState(PageState.UpdatingAddress);
    } else {
      setPageState(PageState.Initial);
    }
  };

  const onDobValidated = (isValid: boolean): void => {
    setUpdateDobError(false);
    setDobIsValid(isValid);
  };

  const handleOnChangeMarketingViaEmail = (value: boolean) => {
    const updatedFormValues = { ...formValues, marketingViaEmail: value };
    setFormValues(updatedFormValues);
  };
  const handleOnChangeMarketingViaPhone = (value: boolean) => {
    const updatedFormValues = { ...formValues, marketingViaPhone: value };
    setFormValues(updatedFormValues);
  };
  const handleOnChangeMarketingViaPost = (value: boolean) => {
    const updatedFormValues = { ...formValues, marketingViaPost: value };
    setFormValues(updatedFormValues);
  };
  const handleOnChangeMarketingViaText = (value: boolean) => {
    const updatedFormValues = { ...formValues, marketingViaTextMessage: value };
    setFormValues(updatedFormValues);
  };

  const validatePersonalDetails = (): boolean => {
    let isValid = true;

    const error: YourDetailsErrors = {
      ...initialFormErrors
    };

    if (dealer === SelectInputHelper.NullValue()) {
      isValid = false;
      error.dealer = dealerErrorMsg;
    }

    if (title === SelectInputHelper.NullValue()) {
      isValid = false;
      error.title = titleErrorMsg;
    }

    if (CommonValidation.isEmpty(gender)) {
      isValid = false;
      error.selectedGender = genderErrorMsg;
    }

    if (CommonValidation.isEmpty(lastName)) {
      isValid = false;
      error.lastName = lastNameRequiredMsg;
    } else if (
      lastName.length < ValidationConstants.Insurance.lastNameMinCharLimit
    ) {
      isValid = false;
      error.lastName = lastNameLimitError;
    } else {
      if (isValidName(lastName)) error.lastName = '';
      else {
        error.lastName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }

    if (CommonValidation.isEmpty(firstName)) {
      isValid = false;
      error.firstName =
        ValidationMessages.InsuranceJourney.firstnameRequiredMsg;
    } else if (
      !CommonValidation.isValidLength(
        firstName,
        ValidationConstants.Insurance.firstNameCharLimit
      )
    ) {
      isValid = false;
      error.firstName = ValidationMessages.InsuranceJourney.firstNameLimitError;
    } else {
      if (isValidName(firstName)) error.firstName = '';
      else {
        error.firstName = ValidationMessages.InsuranceJourney.errorInvalidName;
        isValid = false;
      }
    }
    if (!showManualSearchForm && CommonValidation.isEmpty(userVrn)) {
      error.userVrn = 'Registration number is required';
      isValid = false;
    }
    if (showManualSearchForm) {
      let hasManualSelectionMissing = false;

      if (
        SelectInputHelper.isNullValue(manufacturer) ||
        SelectInputHelper.isEmptyValue(manufacturer)
      ) {
        error.manufacturer = 'Manufacturer is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (
        SelectInputHelper.isNullValue(model) ||
        SelectInputHelper.isEmptyValue(model)
      ) {
        error.model = 'Model is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (
        SelectInputHelper.isNullValue(fuelType) ||
        SelectInputHelper.isEmptyValue(fuelType)
      ) {
        error.fuelType = 'Fuel type is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (CommonValidation.isEmpty(transmissionType)) {
        error.transmission = 'Transmission type is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }
      if (
        SelectInputHelper.isNullValue(numberOfDoors) ||
        SelectInputHelper.isEmptyValue(numberOfDoors)
      ) {
        error.numberOfDoors = 'Number of doors is required';
        isValid = false;
        hasManualSelectionMissing = true;
      }

      if (
        SelectInputHelper.isNullValue(vehicleSpecification) ||
        SelectInputHelper.isEmptyValue(vehicleSpecification)
      ) {
        if (
          vehicleSearchResult === VrnSearchStatus.ManualFound ||
          vehicleSpecificationsOptions.length > 0
        ) {
          error.vehicleSpecification = 'Vehicle specifications is required';
        } else {
          if (!hasManualSelectionMissing) {
            setShowSearchMessage(true);
          }
        }
        isValid = false;
      }
    }

    if (
      marketingViaEmail === false &&
      marketingViaPhone === false &&
      marketingViaPost === false &&
      marketingViaTextMessage === false
    ) {
      error.keepingYouInformedOptions = 'At least one option is required';
      isValid = false;
    }
    if (CommonValidation.isEmpty(coverStartDate)) {
      isValid = false;
      error.coverStartDate = 'Start date is required';
    }
    if (
      !SelectInputHelper.isValidValue(expiryMonth) ||
      CommonValidation.isEmpty(expiryMonth)
    ) {
      isValid = false;
      error.expiryMonth = expiryMonthRequiredMsg;
    }

    if (CommonValidation.isEmpty(mobilePhone)) {
      error.mobilePhone = mobilePhoneRequiredMsg;
      isValid = false;
    } else if (mobilePhone.length < 11) {
      error.mobilePhone = mobilePhoneInvalidMsg;
      isValid = false;
    }

    if (CommonValidation.isNotEmpty(email)) {
      if (!CommonValidation.isValidEmail(email)) {
        error.email = invalidEmailMsg;
        isValid = false;
      }
    } else {
      isValid = false;
      error.email = emailRequiredMsg;
    }

    if (dobIsValid === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobInvalidMsg;
    } else if (CommonValidation.isEmpty(dateOfBirth)) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = dobRequiredMsg;
    } else if (isAgeValid() === false) {
      setUpdateDobError(true);
      isValid = false;
      error.dateOfBirth = ageOutOfRange;
    }

    setFormErrors(error);
    if (isValid) {
      setPageState(PageState.ValidatingAddress);
    }
    return isValid;
  };

  const onContinueClick = () => {
    const isValid = validatePersonalDetails();
    if (isValid) {
      getNewQuote(
        site.brand.brandName,
        site.user?.insuranceDealerCode ?? '',
        site.user.token,
        getNewQuoteSuccess,
        onBackendFailure
      );
    } else {
      console.log('Not Valid Page');
    }
  };

  const getNewQuoteSuccess = (quote: InsuranceJourneyState) => {
    const updatedQuote = getMappedQuote(quote);

    saveQuote(
      updatedQuote,
      site.user.token,
      saveQuoteSuccess,
      onBackendFailure
    );
  };

  const getMappedQuote = (
    quote: InsuranceJourneyState
  ): InsuranceJourneyState => {
    (quote.tiaProductCode = ''),
      (quote.quoteId = 0),
      (quote.policyNumber = ''),
      (quote.savingQuote = true),
      (quote.pageLoading = false),
      (quote.quoteStatus = QuoteStatus.Partial),
      (quote.personalDetails.title = formValues.title),
      (quote.personalDetails.gender = formValues.gender),
      (quote.personalDetails.firstName = formValues.firstName),
      (quote.personalDetails.lastName = formValues.lastName),
      (quote.personalDetails.dateOfBirth = formValues.dateOfBirth),
      (quote.personalDetails.email = formValues.email),
      (quote.personalDetails.homePhone = formValues.homePhone),
      (quote.personalDetails.mobilePhone = formValues.mobilePhone),
      (quote.personalDetails.expiryMonth = formValues.expiryMonth);
    (quote.vehicleInfo.manufacturer = formValues.manufacturer),
      (quote.vehicleInfo.model = formValues.model),
      (quote.vehicleInfo.fuelType = formValues.fuelType),
      (quote.vehicleInfo.transmissionType = formValues.transmissionType),
      (quote.vehicleInfo.numberOfDoors = formValues.numberOfDoors),
      (quote.vehicleInfo.vehicle = formValues.vehicle),
      (quote.vehicleInfo.vehicleSpecification =
        formValues.vehicleSpecification);
    (quote.coverDetails.coverStartDate = parsedCoverStartDate),
      (quote.confirmationDetails.marketingViaPost =
        formValues.marketingViaPost),
      (quote.confirmationDetails.marketingViaPhone =
        formValues.marketingViaPhone),
      (quote.confirmationDetails.marketingViaEmail =
        formValues.marketingViaEmail),
      (quote.confirmationDetails.marketingViaTextMessage =
        formValues.marketingViaTextMessage);
    return quote;
  };

  const saveQuoteSuccess = (quote: InsuranceJourneyState) => {
    if (quote.quoteId > 0) {
      console.log(JSON.stringify(quote));
      const path: string = setCountryCode(
        PageAbsolutePath.Insurance.ExcessConfirmation,
        site.countryCode
      );
      const excessVoucherNumber = quote.quoteId + '-01';
      navigate(path, { state: { voucherReference: excessVoucherNumber } });
    }
  };

  //find vehicle
  const fetchVehicleSuccess = (userVehicle: VehicleType) => {
    if (
      userVehicle.registrationDate === '' ||
      userVehicle.registrationDate === null
    ) {
      setVehicleSearchResult(VrnSearchStatus.VrnNotFound);
      const updatedFormValues = { ...formValues, vehicle: undefined };
      setFormValues(updatedFormValues);
      setShowManualSearchForm(true);
    } else {
      setVehicleSearchResult(VrnSearchStatus.VrnFound);
      const updatedFormValues = { ...formValues, vehicle: userVehicle };
      setFormValues(updatedFormValues);
      setShowManualSearchForm(false);
    }
  };

  const fetchVehicleFailure = () => {
    setVehicleSearchResult(VrnSearchStatus.VrnNotFound);
    const updatedFormValues = { ...formValues, vehicle: undefined };
    setFormValues(updatedFormValues);
    setShowManualSearchForm(true);
  };

  const loadVehicleByVrn = async () => {
    const controller = new AbortController();

    await new VehicleApi().getRegisteredVehicles(
      userVrn,
      site.user.token,
      fetchVehicleSuccess,
      fetchVehicleFailure,
      controller.signal
    );

    return () => {
      controller.abort();
    };
  };

  const onFindVehicle = (): void => {
    setShowSearchMessage(false);
    loadVehicleByVrn();
    const brandName = site.brand.brandName.toUpperCase();
    const updatedFormValues = { ...formValues, manufacturer: brandName };
    setFormValues(updatedFormValues);
  };
  //finde vehicle end

  const onChangingTransmissionType = (value: string) => {
    const updatedFormValues = { ...formValues, transmissionType: value };
    setFormValues(updatedFormValues);
    setVehicleSearchResult(VrnSearchStatus.NoSearch);
  };
  const brannds: string[] = ['genesis', 'polestar'];
  const findBrand = brannds.find((str) =>
    str.includes(site.brand.brandName.toLowerCase())
  );
  return (
    <div>
      {pageName == 'ServiceExcess' && !findBrand && (
        <>
          <h4 className='section-header m1-top'>
            Select Your {site.brand.brandName} dealer
          </h4>
          <div className='table-flex-layout'>
            <div className='form-container'>
              <div className='two-col'>
                <div>
                  <div className='control'>
                    <SelectInput
                      name='dealer'
                      value={dealer}
                      options={dealers}
                      onChange={(value) => onSelectChange(value, 'dealer')}
                      forceDefault
                      addDefaultOption
                      error={formErrors.dealer}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
      <h4 className='section-header m1-top'>Your details</h4>
      <div className='table-flex-layout'>
        <div className='form-container'>
          <div className='two-col'>
            <div>
              <div className='control-label'>
                {t('personal-details.title-label')}
              </div>
              <div className='control'>
                <SelectInput
                  name='title'
                  value={title}
                  options={titleOptions}
                  onChange={(value) => onSelectChange(value, 'title')}
                  forceDefault
                  addDefaultOption
                  error={formErrors.title}
                />
              </div>
            </div>
          </div>
          <div className='two-col'>
            <div>
              <div className='max-content'>
                <GenderSelection
                  genderSelectionError={formErrors.selectedGender}
                  selectedGender={gender}
                  genderList={genderOptions}
                  setSelectedGender={onChangeGenderSelection}
                />
              </div>
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>
              {t('personal-details.first-name-label')}
            </div>
            <div className='control'>
              <TextInput
                name='firstname'
                placeholder='First name'
                value={firstName}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'firstName')
                }
                error={formErrors.firstName}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>
              {t('personal-details.lastname-label')}
            </div>
            <div className='control'>
              <TextInput
                name='lastname'
                placeholder='Last name'
                value={lastName}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'lastName')
                }
                error={formErrors.lastName}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>
              {t('personal-details.date-of-birth-label')}
            </div>
            <div className='control'>
              <DateSelector
                value={dateOfBirth}
                minDate={minDob}
                maxDate={maxDob}
                invalidDateMessage={formErrors.dateOfBirth}
                updateDobError={updateDobError}
                culture={site.cultureCode}
                onChange={(value) => onChangeDateOfBirth(value)}
                onValidated={(isValid) => onDobValidated(isValid)}
                name={'dob'}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>
              {t('personal-details.email-address-label')}
            </div>
            <div className='control'>
              <TextInput
                name='email'
                placeholder='Email address'
                value={email}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  onTextChange(e, 'email')
                }
                error={formErrors.email}
              />
              {pageName == 'ServiceExcess' && (
                <span>
                  Important: We have a paperless approach, so this is where
                  we’ll send all documents, such as your Excess voucher. Please
                  use a valid personal email address and check that it’s typed
                  accurately
                </span>
              )}
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label'>
              {t('personal-details.home-phone')}
            </div>
            <div className='control'>
              <TextInput
                name='homePhone'
                placeholder='Home phone'
                maxLength={11}
                value={homePhone}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  handlePhoneNumberChange(e, 'homePhone')
                }
                readOnly={false}
              />
            </div>
          </div>
          <div className='two-col'>
            <div className='control-label help-text'>
              {t('personal-details.mobile-phone')}
              <ClickableHelpText
                helpText={InsuranceJourneyHelperText.Pages.AboutYou.MobilePhone}
              />
            </div>
            <div className='control'>
              <TextInput
                name='mpbilePhone'
                placeholder='Mobile phone'
                maxLength={11}
                value={mobilePhone}
                onChange={(e: React.FormEvent<HTMLInputElement>) =>
                  handlePhoneNumberChange(e, 'mobilePhone')
                }
                error={formErrors.mobilePhone}
                readOnly={false}
              />
            </div>
          </div>
          <div>
            <AddressManager
              findAddressTitle='Postcode'
              initialAddress={overnightParkedAddress}
              update={pageState === PageState.UpdatingAddress}
              validate={pageState === PageState.ValidatingAddress}
              token={site.user.token}
              onAddressUpdate={handleAddressUpdate}
              onAddressValidated={handleAddressValidate}
              allowManualEntry
            />
          </div>
          <div className='one-col'>
            <div className='control-label'>Registration number</div>
            <div className='flex-row'>
              <div className='control flex-column'>
                <TextInput
                  placeholder='Registration number'
                  name='registration-number'
                  className='input-text w250'
                  value={userVrn}
                  onChange={(e: React.FormEvent<HTMLInputElement>) =>
                    onTextChange(e, 'userVrn')
                  }
                  error={formErrors.userVrn}
                  readOnly={false}
                />
              </div>
            </div>
            {
              <div className='one-col m1-top'>
                <ButtonContinue
                  name='find-vehicle'
                  text='Find vehicle'
                  onClick={onFindVehicle}
                  additionalClassNames='cms-btn-find btn-find'
                />
              </div>
            }
          </div>

          {vehicleSearchResult === VrnSearchStatus.VrnNotFound && (
            <div className='one-col error-style'>
              Vehicle details not found, please select model from the list below
              and click Find vehicle
            </div>
          )}
          {showManualSearchForm && (
            <>
              <div className='two-col'>
                <div className='control-label help-text'>
                  Manufacturer
                  <ClickableHelpText
                    helpText={getDynamicHelpText(
                      InsuranceJourneyHelperText.Pages.VehicleDetails
                        .Manufacturer,
                      '[Brand]',
                      site.brand.brandName
                    )}
                  />
                </div>
                <div className='control'>
                  <SelectInput
                    name='manufacturer'
                    className='input-text w250'
                    value={manufacturer}
                    options={manufacturersOptions}
                    onChange={onChangingManufacturer}
                    addDefaultOption
                    error={formErrors.manufacturer}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>Model</div>
                <div className='control'>
                  <SelectInput
                    name='Model'
                    className='input-text w250'
                    value={model}
                    options={modelsOptions}
                    onChange={(value) => onSelectChange(value, 'model')}
                    addDefaultOption
                    allowZeroOptions
                    error={formErrors.model}
                  />
                </div>
              </div>
              <div className='two-col'>
                <div className='control-label'>Fuel type</div>
                <div className='control'>
                  <SelectInput
                    name='fuelType'
                    className='input-text w250'
                    value={fuelType}
                    options={fuelTypes}
                    onChange={(value) => onSelectChange(value, 'fuelType')}
                    addDefaultOption
                    error={formErrors.fuelType}
                  />
                </div>
              </div>
              <div className='two-col'>
                <RadioOptions
                  errorMessage={formErrors.transmission}
                  selectedOption={
                    !transmissionType
                      ? ''
                      : transmissionType === 'M'
                        ? 'Manual'
                        : 'Automatic'
                  }
                  optionsList={transmissionList}
                  setSelectedOption={(value) =>
                    onChangingTransmissionType(value === 'Manual' ? 'M' : 'A')
                  }
                  label={'Transmission'}
                  name='transmission'
                />
              </div>
              <div className='two-col'>
                <div className='control-label'>Number of doors</div>
                <div className='flex-row'>
                  <div className='control flex-column'>
                    <SelectInput
                      name='numberOfDoors'
                      className='input-text w250'
                      value={numberOfDoors}
                      options={numberOfDoorsValues}
                      onChange={(value) =>
                        onSelectChange(value, 'numberOfDoors')
                      }
                      addDefaultOption
                      error={formErrors.numberOfDoors}
                    />
                  </div>
                  <div className='one-col m1-top'>
                    <ButtonContinue
                      name='find-vehicle-by-search-options'
                      text='Find vehicle'
                      onClick={onFindVehicleBySelectOptions}
                      additionalClassNames='cms-btn-find btn-find'
                    />
                  </div>
                </div>
              </div>
            </>
          )}
          {showSearchMessage && (
            <div className='one-col error-style'>Please click Find Vehicle</div>
          )}
          {showManualSearchForm && vehicleSpecificationsOptions.length > 0 && (
            <div className='one-col'>
              <div className='control-label help-text'>
                Please select the vehicle specification from the list
                <ClickableHelpText
                  helpText={InsuranceJourneyHelperText.Pages.VehicleDetails.VehicleSpecification.replace(
                    '[Phone]',
                    site.brand.brandPhoneNumbers.quotesAndRenewalsNumber
                  )}
                />
              </div>
              <div className='control'>
                <SelectInput
                  name='vehicle-specifications'
                  className='input-text w250'
                  value={vehicleSpecification}
                  options={vehicleSpecificationsOptions}
                  onChange={(value) =>
                    onSelectChange(value, 'vehicleSpecification')
                  }
                  addDefaultOption
                  error={formErrors.vehicleSpecification}
                />
              </div>
            </div>
          )}
          {((vrnKnown && vehicle?.modelType && !showManualSearchForm) ||
            vehicleSearchResult === VrnSearchStatus.VrnFound) && (
            <div className='one-col'>
              We have found your vehicle details: <br />
              {vehicle?.modelType}
            </div>
          )}
          {vehicleSearchResult === VrnSearchStatus.ManualNotFound && (
            <div className='one-col error-style'>No matching vehicles</div>
          )}

          <div className='one-col'>
            <div className='control-label'>
              When would you like your Excess Return voucher to start?
            </div>
            <div className='control'>
              <DateInput
                id='coverStart-date-id'
                name='coverStart-date-date'
                value={coverStartDate}
                useJson
                forceTimeReset
                minimumDate={today}
                onDateChanged={(e: React.FormEvent<HTMLInputElement>) =>
                  onDateChange(e, 'coverStartDate')
                }
                culture={site.cultureCode}
                error={formErrors.coverStartDate}
              />
            </div>
          </div>
          <div>
            <div className='control-label'>
              If you would like to receive a {brandDisplayName} Car Insurance
              quote for your next renewal, please select your renewal month
            </div>
            <div className='control'>
              <SelectInput
                name='expiryMonth'
                className='input-text w250'
                value={expiryMonth}
                onChange={onChangeExpiryMonth}
                options={monthNames}
                forceDefault
                addDefaultOption
                error={formErrors.expiryMonth}
              />
            </div>
          </div>
          <div className='one-col modify-label-order'>
            <div className='control-label'>
              How would you like us to contact you?
            </div>
            <Checkbox
              id='chk-marketing-post'
              checked={marketingViaPost}
              text='Post'
              onChange={() => handleOnChangeMarketingViaPost(!marketingViaPost)}
            />
            <Checkbox
              id='chk-marketing-phone'
              checked={marketingViaPhone}
              text='Telephone'
              onChange={() =>
                handleOnChangeMarketingViaPhone(!marketingViaPhone)
              }
            />
            <Checkbox
              id='chk-marketing-email'
              checked={marketingViaEmail}
              text='Email (or other electronic means)'
              onChange={() =>
                handleOnChangeMarketingViaEmail(!marketingViaEmail)
              }
            />
            <Checkbox
              id='chk-marketing-text'
              checked={marketingViaTextMessage}
              text='Text message'
              onChange={() =>
                handleOnChangeMarketingViaText(!marketingViaTextMessage)
              }
            />
            {formErrors.keepingYouInformedOptions && (
              <div className='error-style'>
                {formErrors.keepingYouInformedOptions}
              </div>
            )}
          </div>
          <div>
            By submitting this information, you agree to the terms of our
            <Link
              id='lnkTermsOfBusiness'
              className='in-text'
              to={PageAbsolutePath.Insurance.FairUseNoticePage}>
              Fair use notice.
            </Link>
          </div>
          <div className='flex-row'>
            <Link
              id='lnkTermsOfBusiness'
              className='in-text'
              to={PageAbsolutePath.Insurance.TermsAndConditions}>
              Excess Voucher Terms and Conditions
            </Link>
          </div>
        </div>
        <div className='flex-row form-nav'>
          <ButtonContinue
            name='btn-save-quote'
            text='Submit'
            onClick={onContinueClick}
            additionalClassNames='btn-save'
          />
        </div>
      </div>
      <Modal
        role='non-scheme-modal-popup'
        isOpen={showNonSchemePopup}
        className={`insurance-${site.brand.brandName}`}
        toggle={() => setShowNonSchemePopup(false)}>
        <ModalHeader toggle={() => setShowNonSchemePopup(false)}>
          Please Note
        </ModalHeader>
        <ModalBody>
          The vehicle to be insured is NON-SCHEME. Please advise the customer to
          contact Original Insurance on 0800 035 2715
        </ModalBody>
        <ModalFooter>
          <ButtonContinue
            name='modal-btn-continue'
            text={t('customer-product-page.button-quote-expired-continue')}
            onClick={() => setShowNonSchemePopup(false)}
            additionalClassNames='btn-ok'
          />
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default YourDetails;
